import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useBrandKit } from '../contexts/BrandKitContext';
import ColorPicker from './ColorPicker';
import AssetUploader from './AssetUploader';

const { FiSave, FiX, FiPalette, FiType, FiImage } = FiIcons;

const BrandKitForm = ({ editingKit, onClose }) => {
  const { createBrandKit, updateBrandKit } = useBrandKit();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    colors: {
      primary: '#03245E',
      secondary: '#D7B740',
      accent: '#E2C164',
      background: '#F3F7FA',
      text: '#111827',
      highlight: '#F7EFE2'
    },
    fonts: {
      primary: 'Helvetica World',
      secondary: 'Montserrat',
      heading: 'League Spartan',
      title: 'Anton',
      subtitle: 'Montserrat',
      highlight: 'Gistesy'
    },
    logos: [],
    watermarks: []
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (editingKit) {
      setFormData({
        name: editingKit.name || '',
        description: editingKit.description || '',
        colors: editingKit.colors || {
          primary: '#03245E',
          secondary: '#D7B740',
          accent: '#E2C164',
          background: '#F3F7FA',
          text: '#111827',
          highlight: '#F7EFE2'
        },
        fonts: editingKit.fonts || {
          primary: 'Helvetica World',
          secondary: 'Montserrat',
          heading: 'League Spartan',
          title: 'Anton',
          subtitle: 'Montserrat',
          highlight: 'Gistesy'
        },
        logos: editingKit.logos || [],
        watermarks: editingKit.watermarks || []
      });
    }
  }, [editingKit]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (colorKey, color) => {
    setFormData(prev => ({ ...prev, colors: { ...prev.colors, [colorKey]: color } }));
  };

  const handleFontChange = (fontKey, font) => {
    setFormData(prev => ({ ...prev, fonts: { ...prev.fonts, [fontKey]: font } }));
  };

  const handleAssetsChange = (type, assets) => {
    setFormData(prev => ({ ...prev, [type]: assets }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name for your brand kit');
      return;
    }
    setSaving(true);
    try {
      if (editingKit) {
        await updateBrandKit(editingKit.id, formData);
      } else {
        await createBrandKit(formData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save brand kit:', error);
      alert('Failed to save brand kit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiType },
    { id: 'colors', label: 'Colors', icon: FiPalette },
    { id: 'assets', label: 'Assets', icon: FiImage }
  ];

  const fontOptions = [
    'Anton', 'Gistesy', 'League Spartan', 'Helvetica World', 'Montserrat', 'Inter', 'Roboto', 'Open Sans', 'Lato'
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {editingKit ? 'Edit Brand Kit' : 'Create Brand Kit'}
          </h2>
          <p className="text-gray-600">Define your brand identity</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Kit Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., My Company Brand"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe your brand identity..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fonts
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(formData.fonts).map(([key, font]) => (
                  <div key={key}>
                    <label className="block text-xs text-gray-600 mb-1 capitalize">
                      {key} Font
                    </label>
                    <select
                      value={font}
                      onChange={(e) => handleFontChange(key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {fontOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'colors' && (
          <div>
            <ColorPicker colors={formData.colors} onChange={handleColorChange} />
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-8">
            <AssetUploader
              type="logos"
              assets={formData.logos}
              onChange={(assets) => handleAssetsChange('logos', assets)}
              title="Logos"
              description="Upload your brand logos in various formats"
            />
            <AssetUploader
              type="watermarks"
              assets={formData.watermarks}
              onChange={(assets) => handleAssetsChange('watermarks', assets)}
              title="Watermarks"
              description="Upload watermark images for document branding"
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Brand Kit'}</span>
        </button>
      </div>
    </motion.div>
  );
};

export default BrandKitForm;